import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Bot, ArrowLeft, Smartphone, Save } from 'lucide-react';
import api from '../../services/api';
import { GlassButton } from '../../components/ui/GlassButton';

const GATEWAY_OPTIONS = [
    { value: '', label: 'Usar padrão do sistema' },
    { value: 'podpay', label: 'PodPay v1' },
    { value: 'slimpay', label: 'Slimpay' }
];

interface BotDetailData {
    id: string;
    name?: string;
    slug?: string;
    username?: string;
    status?: string;
    owner_phone?: string;
    owner_name?: string;
    owner_email?: string;
    effective_gateway?: string;
    global_gateway?: string;
    active_gateway?: string | null;
    description?: string;
    enable_support?: boolean;
    created_at?: string;
    [key: string]: any;
}

export const BotDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [bot, setBot] = useState<BotDetailData | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [adquirente, setAdquirente] = useState<string>('');
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    useEffect(() => {
        if (!id) return;
        const load = async () => {
            try {
                setLoading(true);
                const res = await api.get(`/banana/bots/${id}`);
                const data = res.data?.bot;
                if (data) {
                    setBot(data);
                    setAdquirente(data.active_gateway === 'slimpay' ? 'slimpay' : (data.active_gateway === 'podpay' ? 'podpay' : ''));
                } else {
                    setBot(null);
                }
            } catch (e) {
                console.error('Erro ao carregar bot:', e);
                setBot(null);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    const handleSaveAdquirente = async () => {
        if (!id) return;
        setSaving(true);
        setToast(null);
        try {
            await api.patch(`/banana/bots/${id}`, {
                active_gateway: adquirente === '' ? null : adquirente
            });
            setBot(prev => prev ? { ...prev, active_gateway: adquirente || null, effective_gateway: adquirente || prev.global_gateway || 'podpay' } : null);
            setToast({ msg: 'Adquirente atualizada.', type: 'success' });
        } catch (e: any) {
            setToast({ msg: e.response?.data?.error || 'Erro ao salvar.', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 text-white opacity-50">Carregando detalhes do bot...</div>
        );
    }

    if (!bot) {
        return (
            <div className="p-6">
                <button
                    type="button"
                    onClick={() => navigate('/banana/bots')}
                    className="text-gray-400 hover:text-white text-sm flex items-center gap-2 mb-6"
                >
                    <ArrowLeft size={18} /> Voltar
                </button>
                <p className="text-gray-500">Bot não encontrado.</p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-2xl">
            <button
                type="button"
                onClick={() => navigate('/banana/bots')}
                className="text-gray-400 hover:text-white text-sm flex items-center gap-2 mb-6"
            >
                <ArrowLeft size={18} /> Voltar ao monitoramento
            </button>

            {toast && (
                <div className={`mb-4 px-4 py-2 rounded-xl text-sm ${toast.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {toast.msg}
                </div>
            )}

            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
                <h1 className="text-2xl font-bold text-white flex items-center gap-2 mb-8">
                    <Bot size={28} className="text-orange-500" /> Detalhes do bot
                </h1>

                <dl className="space-y-6 text-sm">
                    <div>
                        <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">Nome</dt>
                        <dd className="text-white text-lg">{bot.name ?? '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">Slug</dt>
                        <dd className="text-gray-300 font-mono text-sm break-all">{bot.slug ?? '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">Username (Telegram)</dt>
                        <dd className="text-gray-300">@{bot.username ?? '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">Status</dt>
                        <dd>
                            <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-bold ${['online', 'active', 'running'].includes(bot.status || '') ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                {(bot.status || 'offline').toUpperCase()}
                            </span>
                        </dd>
                    </div>
                    <div>
                        <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1 flex items-center gap-1"><Smartphone size={12} /> Dono (telefone)</dt>
                        <dd className="text-white">{bot.owner_phone ?? '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">Nome do dono</dt>
                        <dd className="text-gray-300">{bot.owner_name ?? '—'}</dd>
                    </div>
                    {bot.owner_email && (
                        <div>
                            <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">Email do dono</dt>
                            <dd className="text-gray-300 break-all">{bot.owner_email}</dd>
                        </div>
                    )}
                    {bot.description && (
                        <div>
                            <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">Descrição</dt>
                            <dd className="text-gray-300 whitespace-pre-wrap">{bot.description}</dd>
                        </div>
                    )}
                    <div>
                        <dt className="text-[10px] text-gray-500 font-bold uppercase mb-1">ID do bot</dt>
                        <dd className="text-gray-500 font-mono text-xs">{bot.id}</dd>
                    </div>
                </dl>

                <div className="mt-8 pt-8 border-t border-white/10">
                    <h2 className="text-base font-semibold text-white mb-4">Adquirente (gateway de pagamento)</h2>
                    <p className="text-xs text-gray-500 mb-4">
                        Define qual adquirente gera o PIX para este bot. Se &quot;Usar padrão do sistema&quot;, será usado o gateway global configurado em Adquirentes.
                    </p>
                    <div className="flex flex-wrap items-center gap-4">
                        <select
                            value={adquirente}
                            onChange={e => setAdquirente(e.target.value)}
                            className="bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:border-orange-500/50 outline-none min-w-[200px]"
                        >
                            {GATEWAY_OPTIONS.map(opt => (
                                <option key={opt.value || 'default'} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <GlassButton onClick={handleSaveAdquirente} loading={saving} disabled={saving}>
                            {saving ? null : <Save size={16} />}
                            Salvar adquirente
                        </GlassButton>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-2">
                        Em uso agora: <strong className="text-orange-500">{adquirente ? (adquirente === 'slimpay' ? 'Slimpay' : 'PodPay v1') : `Padrão do sistema (${bot.global_gateway === 'slimpay' ? 'Slimpay' : 'PodPay v1'})`}</strong>
                    </p>
                </div>

                <div className="mt-8 flex gap-4">
                    <GlassButton onClick={() => navigate('/banana/bots')}>
                        Voltar ao monitoramento
                    </GlassButton>
                </div>
            </div>
        </div>
    );
};
